import React, { useState, useEffect, useCallback } from 'react';
import {
  Megaphone, User, Globe, CheckCircle,
  XCircle, Volume2, Loader2, RefreshCw,
  Play
} from 'lucide-react';
import toast from 'react-hot-toast';
import { useSearchParams } from 'react-router-dom';
import { airportAnnouncementsAPI, announcementZonesAPI, gatesAPI } from '../../services/api';
import { useSupervisorSocket } from '../../hooks/useGateSocket';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';
import { isEnglishOnly } from '../../utils/languageLabel';

// ── Constants ─────────────────────────────────────────────────────────────────

const PRIORITY_CFG = {
  normal:    { label: 'Normal',    tone: 'neutral' },
  urgent:    { label: 'Urgent',    tone: 'orange' },
  emergency: { label: 'Emergency', tone: 'danger' },
};

// 'broadcasting' was a one-off solid-blue badge here (light-tint everywhere
// else this status renders) — unified to the same convention used on every
// other page that shows it: info tone + pulsing dot, matching how
// active/playing states render elsewhere.
const STATUS_CFG = {
  pending:      { label: 'Pending',      tone: 'yellow' },
  generating:   { label: 'Generating',   tone: 'info' },
  ready:        { label: 'Ready',        tone: 'success' },
  broadcasting: { label: 'Broadcasting', tone: 'info', pulse: true },
  played:       { label: 'Played',       tone: 'neutral' },
  rejected:     { label: 'Rejected',     tone: 'danger' },
  cancelled:    { label: 'Cancelled',    tone: 'neutral' },
};

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return `${s}s ago`;
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  return `${Math.floor(s / 3600)}h ago`;
}

// Human-readable form of whatever timing the requester (gate/zone operator) picked
// on their end — CR 8.4 lets the approver retain this as-is or override it, so it
// needs to actually be visible here, not just silently honored under the hood.
function describeRequestedTiming(ann) {
  if (!ann) return '';
  if (ann.timing_type === 'clock' && ann.scheduled_at) {
    return `At ${new Date(ann.scheduled_at).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}`;
  }
  if (ann.timing_type === 'relative') {
    const m = Number(ann.relative_minutes) || 0;
    return m === 0 ? 'As soon as possible' : `${m} minute${m === 1 ? '' : 's'} after approval`;
  }
  if (ann.timing_type === 'sequence') {
    return ann.insert_after_id ? 'After a specific announcement in the queue' : 'First in the queue';
  }
  return 'Not specified';
}

// datetime-local inputs need "YYYY-MM-DDTHH:mm" in local time, not the ISO/UTC
// string the API returns.
function toLocalDatetimeInputValue(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const pad = n => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ── Main component ────────────────────────────────────────────────────────────

export default function AdminAirportAnnouncementsPage() {
  const [searchParams] = useSearchParams();
  const [pending, setPending] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState('pending'); // 'pending' | 'history'
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Approval state
  const [zones, setZones] = useState([]);
  const [gates, setGates] = useState([]);
  const [selectedZones, setSelectedZones] = useState([]);
  const [selectedGates, setSelectedGates] = useState([]);
  const [scheduleMode, setScheduleMode] = useState('now'); // 'now' | 'later'
  const [scheduledAt, setScheduledAt] = useState('');
  const [adminNotes, setAdminNotes] = useState('');
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [rejectNote, setRejectNote] = useState('');
  const [showRejectForm, setShowRejectForm] = useState(false);

  const loadPending = useCallback(async () => {
    try {
      const res = await airportAnnouncementsAPI.getPending();
      const list = res.data.data || [];
      setPending(list);
      // Clear selected if it's no longer in the pending list (was approved/rejected/cancelled)
      setSelected(prev => prev ? (list.find(a => a.id === prev.id) || null) : null);
    } catch { toast.error('Failed to load pending queue'); }
    finally { setLoading(false); }
  }, []);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      // The backend filters status by exact match, so a comma-joined value never
      // matches anything — fetch everything recent and filter out 'pending' client-side
      // (same approach already used on the Supervisor equivalent of this page).
      const res = await airportAnnouncementsAPI.getAll({ limit: 50 });
      const all = res.data.data || [];
      setHistory(all.filter(a => a.status !== 'pending'));
    } catch { toast.error('Failed to load history'); }
    finally { setHistoryLoading(false); }
  }, []);

  useEffect(() => {
    loadPending();
    Promise.all([announcementZonesAPI.getAll(), gatesAPI.getAll()])
      .then(([zRes, gRes]) => {
        setZones(zRes.data.data || []);
        setGates(gRes.data.data || []);
      })
      .catch(() => {});
  }, [loadPending]);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  // Deep link support — e.g. /admin/airport-announcements?id=123 from the Belt
  // Assignments page, so "view the announcement" links land on the right item.
  useEffect(() => {
    const id = searchParams.get('id');
    if (!id) return;
    airportAnnouncementsAPI.getById(id)
      .then(res => {
        const ann = res.data.data;
        setTab(['pending', 'rejected'].includes(ann.status) ? 'pending' : 'history');
        selectAnnouncement(ann);
      })
      .catch(() => toast.error('Could not load that announcement'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // Polling fallback — refresh pending list every 20s regardless of socket
  useEffect(() => {
    const id = setInterval(() => {
      loadPending();
      if (tab === 'history') loadHistory();
    }, 20000);
    return () => clearInterval(id);
  }, [tab, loadPending, loadHistory]);

  // Live updates via WebSocket (primary)
  useSupervisorSocket({
    onEvent: (evt) => {
      if (['announcement:submitted', 'announcement:status_change', 'announcement:status'].includes(evt)) {
        loadPending();
        if (tab === 'history') loadHistory();
      }
    },
  });

  function selectAnnouncement(ann) {
    setSelected(ann);
    // Pre-populate targets from the announcement's existing targets if any
    const existingTargets = ann.targets || [];
    if (existingTargets.length > 0) {
      setSelectedZones(existingTargets.filter(t => t.target_type === 'zone').map(t => t.target_id));
      setSelectedGates(existingTargets.filter(t => t.target_type === 'gate').map(t => String(t.target_id)));
    } else if (ann.suggested_gate_id) {
      // CR 4.3 — ad-hoc/template requests submitted from within a specific gate
      // session carry that gate as suggested_gate_id; default the approver's
      // target to it instead of Airport Wide, matching what the operator asked for.
      setSelectedZones([]);
      setSelectedGates([String(ann.suggested_gate_id)]);
    } else {
      // Default to Airport Wide zone when no targets or gate suggestion are pre-set
      const airportWide = zones.find(z => z.zone_code === 'ALL' || z.zone_type === 'airport');
      setSelectedZones(airportWide ? [airportWide.zone_code] : []);
      setSelectedGates([]);
    }
    // CR 8.4 — default to keeping whatever timing the operator requested; the
    // "later" datetime input is pre-filled too, purely so it starts from their
    // original clock choice if the admin switches to "later" then back.
    setScheduleMode('requested');
    setScheduledAt(ann.timing_type === 'clock' ? toLocalDatetimeInputValue(ann.scheduled_at) : '');
    setAdminNotes('');
    setPreviewUrl(null);
    setShowRejectForm(false);
    setRejectNote('');
  }

  function toggleZone(zone) {
    const id = zone.zone_code;
    setSelectedZones(prev =>
      prev.includes(id) ? prev.filter(z => z !== id) : [...prev, id]
    );
  }

  function toggleGate(gate) {
    const id = String(gate.id);
    setSelectedGates(prev =>
      prev.includes(id) ? prev.filter(g => g !== id) : [...prev, id]
    );
  }

  async function handlePreview() {
    if (!selected) return;
    setPreviewing(true);
    setPreviewUrl(null);
    try {
      const res = await airportAnnouncementsAPI.preview(selected.id);
      setPreviewUrl(res.data.data.audioUrl);
    } catch { toast.error('Preview failed'); }
    finally { setPreviewing(false); }
  }

  async function handleApprove() {
    if (!selected) return;
    const targets = [
      ...selectedZones.map(code => {
        const z = zones.find(z => z.zone_code === code);
        return { target_type: 'zone', target_id: code, target_label: z?.zone_name || code };
      }),
      ...selectedGates.map(gid => {
        const g = gates.find(g => String(g.id) === gid);
        return { target_type: 'gate', target_id: gid, target_label: `Gate ${g?.gate_code || gid}` };
      }),
    ];
    if (targets.length === 0) {
      toast.error('Select at least one target zone or gate');
      return;
    }
    if (scheduleMode === 'later' && !scheduledAt) {
      toast.error('Choose a broadcast time');
      return;
    }
    // CR 8.4 — retain the operator's original timing choice by default, or let the
    // admin explicitly override it with "now" (immediate) or a new "later" time.
    // Sent explicitly (rather than leaving keys out and relying on the backend's
    // own ?? ann.* fallback) so what's on screen always matches what's submitted.
    const timing = scheduleMode === 'now'
      ? { timing_type: 'relative', relative_minutes: 0, scheduled_at: null, insert_after_id: null }
      : scheduleMode === 'later'
        ? { timing_type: 'clock', scheduled_at: new Date(scheduledAt).toISOString(), relative_minutes: null, insert_after_id: null }
        : {
            timing_type: selected.timing_type,
            relative_minutes: selected.relative_minutes,
            insert_after_id: selected.insert_after_id,
            scheduled_at: selected.scheduled_at,
          };
    setApproving(true);
    try {
      await airportAnnouncementsAPI.approve(selected.id, {
        targets,
        ...timing,
        admin_notes: adminNotes || null,
      });
      toast.success('Approved — audio generating and broadcast queued');
      setSelected(null);
      loadPending();
    } catch (err) {
      if (!err._toasted) toast.error('Approval failed');
    } finally { setApproving(false); }
  }

  async function handleReject() {
    if (!selected) return;
    setRejecting(true);
    try {
      await airportAnnouncementsAPI.reject(selected.id, { admin_notes: rejectNote });
      toast.success('Announcement rejected');
      setSelected(null);
      loadPending();
    } catch (err) {
      if (!err._toasted) toast.error('Rejection failed');
    } finally { setRejecting(false); }
  }

  // ── Render ───────────────────────────────────────────────────────────────

  return (
    <div className="flex h-[calc(100vh-56px)] overflow-hidden">

      {/* ── Left pane: list ────────────────────────────────────────────────── */}
      <div className="w-72 flex-shrink-0 flex flex-col border-r border-gray-200 bg-white">
        <div className="px-4 pt-4 pb-2 border-b border-gray-100">
          <h1 className="text-base font-bold text-gray-900">Airport Announcements</h1>
          <div className="flex gap-1 mt-2">
            {['pending', 'history'].map(t => (
              <button key={t} onClick={() => setTab(t)}
                className={`flex-1 py-1.5 rounded-lg text-xs font-semibold transition-colors ${tab === t ? 'bg-blue-700 text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
                {t === 'pending' ? `Pending ${pending.length > 0 ? `(${pending.length})` : ''}` : 'History'}
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {tab === 'pending' && (
            loading ? (
              <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
            ) : pending.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <CheckCircle size={32} className="mx-auto mb-2 text-gray-200" />
                <p className="text-sm">All clear — no pending requests</p>
              </div>
            ) : (
              pending.map(ann => {
                const pc = PRIORITY_CFG[ann.priority] || PRIORITY_CFG.normal;
                const isSelected = selected?.id === ann.id;
                return (
                  <button key={ann.id} onClick={() => selectAnnouncement(ann)}
                    className={`w-full text-left px-4 py-3 border-b border-gray-50 hover:bg-gray-50 transition-colors ${isSelected ? 'bg-blue-50 border-l-2 border-l-blue-600' : ''}`}>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className={`w-2 h-2 rounded-full flex-shrink-0 ${pc.dot}`} />
                      <span className="text-xs font-semibold text-gray-800 truncate flex-1">{ann.title}</span>
                      {ann.announcement_type === 'passenger_paging' && (
                        <User size={11} className="text-purple-500 flex-shrink-0" />
                      )}
                    </div>
                    <p className="text-xs text-gray-400 pl-4">
                      {ann.requester?.name} · {timeAgo(ann.createdAt)}
                    </p>
                  </button>
                );
              })
            )
          )}

          {tab === 'history' && (
            historyLoading ? (
              <div className="flex justify-center py-8"><Loader2 size={20} className="animate-spin text-gray-300" /></div>
            ) : history.length === 0 ? (
              <div className="text-center py-12 text-gray-400 text-sm">No history yet</div>
            ) : (
              history.map(ann => {
                const sc = STATUS_CFG[ann.status] || STATUS_CFG.played;
                return (
                  <div key={ann.id} className="px-4 py-3 border-b border-gray-50">
                    <div className="flex items-center gap-2 mb-0.5">
                      <StatusBadge label={sc.label} tone={sc.tone} pulse={sc.pulse} size="sm" />
                      <span className="text-xs text-gray-600 truncate flex-1">{ann.title}</span>
                    </div>
                    <p className="text-xs text-gray-400">{ann.requester?.name} · {timeAgo(ann.createdAt)}</p>
                  </div>
                );
              })
            )
          )}
        </div>
      </div>

      {/* ── Right pane: detail / approval ─────────────────────────────────── */}
      <div className="flex-1 overflow-y-auto bg-gray-50">
        {!selected ? (
          <div className="flex flex-col items-center justify-center h-full text-gray-400">
            <Megaphone size={48} className="mb-3 text-gray-200" />
            <p className="font-medium">Select a request to review</p>
          </div>
        ) : (
          <div className="max-w-2xl mx-auto px-6 py-6 space-y-5">

            {/* Header */}
            <div className="flex items-start gap-3">
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h2 className="text-lg font-bold text-gray-900">{selected.title}</h2>
                  {selected.announcement_type === 'passenger_paging' && (
                    <StatusBadge label="Paging" tone="purple" />
                  )}
                  {(() => { const pc = PRIORITY_CFG[selected.priority]; return (
                    <StatusBadge label={pc.label} tone={pc.tone} />
                  ); })()}
                </div>
                <p className="text-sm text-gray-500 mt-0.5">
                  Submitted by <span className="font-medium">{selected.requester?.name}</span> ({selected.requester?.role}) · {timeAgo(selected.createdAt)}
                </p>
              </div>
              <button onClick={() => loadPending()} className="p-2 rounded-lg hover:bg-gray-100 text-gray-400">
                <RefreshCw size={15} />
              </button>
            </div>

            {/* Paging detail */}
            {selected.pagingDetail && (
              <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 space-y-1.5">
                <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-2">Paging Details</p>
                <Row label="Passenger" value={selected.pagingDetail.passenger_name} />
                {selected.pagingDetail.flight_number && <Row label="Flight" value={selected.pagingDetail.flight_number} />}
                <Row label="Action" value={selected.pagingDetail.paging_action?.replace(/_/g, ' ')} />
                {selected.pagingDetail.action_location && <Row label="Location" value={selected.pagingDetail.action_location} />}
                {selected.pagingDetail.urgency_reason && <Row label="Internal note" value={selected.pagingDetail.urgency_reason} italic />}
              </div>
            )}

            {/* Announcement text */}
            <div className="bg-white border border-gray-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Announcement Text</p>
              <p className="text-sm text-gray-800 leading-relaxed">{selected.english_text}</p>
              <p className="text-xs text-gray-400 mt-2 flex items-center gap-1">
                <Globe size={11} /> {isEnglishOnly(selected.secondary_language) ? 'English only — no translation' : `Will be translated to ${selected.secondary_language} automatically`}
                {selected.announcement_type === 'passenger_paging' && ' · Repeated 3×'}
              </p>
            </div>

            {/* Audio preview */}
            <div className="flex items-center gap-3">
              <Button variant="dark" icon={Volume2} loading={previewing} onClick={handlePreview}>
                {previewing ? 'Generating…' : 'Preview Audio'}
              </Button>
              {previewUrl && (
                <audio controls src={previewUrl} className="flex-1 h-8" />
              )}
            </div>

            {['pending', 'rejected'].includes(selected.status) ? (
              <>
                {/* Target zones */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-bold text-gray-800 mb-3">Target Zones <span className="text-red-500">*</span></p>
                  <div className="grid grid-cols-2 gap-2 mb-3">
                    {zones.map(zone => {
                      const checked = selectedZones.includes(zone.zone_code);
                      return (
                        <label key={zone.id}
                          className={`flex items-center gap-2 p-2.5 rounded-xl border-2 cursor-pointer transition-all ${checked ? 'border-blue-500 bg-blue-50' : 'border-gray-100 bg-gray-50 hover:border-gray-200'}`}>
                          <input type="checkbox" checked={checked} onChange={() => toggleZone(zone)} className="sr-only" />
                          <div className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                            {checked && <CheckCircle size={12} className="text-white" />}
                          </div>
                          <div>
                            <p className="text-xs font-semibold text-gray-800">{zone.zone_name}</p>
                            <p className="text-xs text-gray-400 capitalize">{zone.zone_type}</p>
                          </div>
                        </label>
                      );
                    })}
                  </div>

                  {/* Specific gates */}
                  <p className="text-xs font-semibold text-gray-600 mb-2">Specific Gates (optional)</p>
                  <div className="max-h-32 overflow-y-auto flex flex-wrap gap-1.5">
                    {gates.map(gate => {
                      const checked = selectedGates.includes(String(gate.id));
                      return (
                        <button key={gate.id} type="button" onClick={() => toggleGate(gate)}
                          className={`px-2.5 py-1 rounded-lg text-xs font-semibold border transition-all ${checked ? 'bg-blue-600 border-blue-600 text-white' : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'}`}>
                          {gate.gate_code}
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Schedule */}
                <div className="bg-white border border-gray-200 rounded-xl p-4">
                  <p className="text-sm font-bold text-gray-800 mb-3">Broadcast Timing</p>
                  <div className="flex flex-col gap-2 mb-3">
                    {[
                      ['requested', `Keep as requested — ${describeRequestedTiming(selected)}`],
                      ['now', 'Broadcast now'],
                      ['later', 'Schedule for later'],
                    ].map(([v, l]) => (
                      <label key={v} className={`flex items-center gap-2 px-3 py-2 rounded-xl border-2 cursor-pointer transition-all ${scheduleMode === v ? 'border-blue-500 bg-blue-50' : 'border-gray-100 hover:border-gray-200'}`}>
                        <input type="radio" name="schedule" value={v} checked={scheduleMode === v} onChange={() => setScheduleMode(v)} className="sr-only" />
                        <div className={`w-4 h-4 rounded-full border-2 flex-shrink-0 flex items-center justify-center ${scheduleMode === v ? 'border-blue-600 bg-blue-600' : 'border-gray-300'}`}>
                          {scheduleMode === v && <div className="w-1.5 h-1.5 bg-white rounded-full" />}
                        </div>
                        <span className="text-sm font-medium text-gray-700">{l}</span>
                      </label>
                    ))}
                  </div>
                  {scheduleMode === 'later' && (
                    <input type="datetime-local" value={scheduledAt} onChange={e => setScheduledAt(e.target.value)}
                      className="w-full border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                  )}
                </div>

                {/* Admin notes */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-1">Notes (optional)</label>
                  <input type="text" value={adminNotes} onChange={e => setAdminNotes(e.target.value)}
                    placeholder="Internal notes visible to other supervisors"
                    className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500" />
                </div>

                {/* Actions */}
                {!showRejectForm ? (
                  <div className="flex gap-3 pb-6">
                    <button onClick={() => setShowRejectForm(true)}
                      className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl border-2 border-red-200 text-red-600 font-semibold text-sm hover:bg-red-50 transition-colors">
                      <XCircle size={15} /> Reject
                    </button>
                    <Button variant="primary" icon={Play} loading={approving} onClick={handleApprove} className="flex-1">
                      {approving ? 'Processing…' : (() => {
                        const isImmediate = scheduleMode === 'now'
                          || (scheduleMode === 'requested' && selected?.timing_type === 'relative' && !Number(selected?.relative_minutes));
                        return isImmediate ? 'Approve & Broadcast' : 'Approve & Schedule';
                      })()}
                    </Button>
                  </div>
                ) : (
                  <div className="bg-red-50 border border-red-200 rounded-xl p-4 pb-6 space-y-3">
                    <p className="text-sm font-semibold text-red-700">Reason for rejection</p>
                    <textarea value={rejectNote} onChange={e => setRejectNote(e.target.value)} rows={2}
                      placeholder="Optional — will be visible to the submitter"
                      className="w-full border border-red-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 resize-none bg-white" />
                    <div className="flex gap-2">
                      <Button variant="ghost" onClick={() => setShowRejectForm(false)} className="flex-1">
                        Cancel
                      </Button>
                      <Button variant="danger" icon={XCircle} loading={rejecting} onClick={handleReject} className="flex-1">
                        Confirm Reject
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              /* Already resolved — no human action needed (auto-approved system announcements
                 land here as 'played' the moment they broadcast; nothing left to approve). */
              <div className="bg-white border border-gray-200 rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-sm font-bold text-gray-800">Delivery</p>
                  {(() => { const sc = STATUS_CFG[selected.status]; return (
                    <StatusBadge label={sc?.label || selected.status} tone={sc?.tone || 'neutral'} pulse={sc?.pulse} />
                  ); })()}
                </div>
                {selected.approved_by && (
                  <Row label="Approved by" value={selected.approver?.name ? `${selected.approver.name} (auto)` : 'System — no human approval needed'} />
                )}
                {selected.played_at && <Row label="Played" value={new Date(selected.played_at).toLocaleString()} />}
                <div>
                  <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1.5">Sent to</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.targets || []).map(t => (
                      <span key={t.id} className="px-2.5 py-1 rounded-lg text-xs font-semibold bg-gray-100 text-gray-600">
                        {t.target_label}
                      </span>
                    ))}
                    {(selected.targets || []).length === 0 && <span className="text-xs text-gray-400">No targets recorded</span>}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function Row({ label, value, italic }) {
  return (
    <div className="flex gap-2 text-sm">
      <span className="text-purple-500 font-medium w-28 flex-shrink-0">{label}:</span>
      <span className={`text-gray-800 ${italic ? 'italic text-gray-500' : ''}`}>{value}</span>
    </div>
  );
}
